import { escapeHtml, formatMenuPrice } from '../format.js';
import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  badge,
  bilingual,
  description,
  fitScale,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  sectionSubtitle,
  sectionTitle,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
  variantLabel,
} from './common.js';

/**
 * Night Bites, four columns: a late-night burger-joint board on flat charcoal. A huge white
 * "MENU" with a script line under it, then four columns split by thin rules, each topped by a
 * line-art icon picked from the section's name and a script heading, with plain uppercase names
 * and prices in the accent colour. Portrait stacks the four columns two by two.
 *
 * The icons, theme and shared pieces of the Night Bites set live here; the other two designs
 * of the set import them.
 */

/* ------------------------------------------------------------------ shared by the set */

export const NB_FAMILY = { id: 'night-bites', name: 'Night Bites' } as const;

export const NB_THEME: MenuTheme = {
  backgroundColor: '#3b3b3b',
  surfaceColor: '#464646',
  textColor: '#f4f4f4',
  primaryColor: '#ffffff',
  accentColor: '#d7e04b',
  fontId: 'modern',
  backgroundMediaId: null,
};

export const NB_VARIANTS: MenuStyleVariant[] = [
  { id: 'lime', name: 'Lime on charcoal', theme: {} },
  {
    id: 'navy',
    name: 'Orange on navy',
    theme: { backgroundColor: '#14213d', surfaceColor: '#1d2d52', textColor: '#f2f2f2', primaryColor: '#ffffff', accentColor: '#fca311' },
  },
  {
    id: 'neon',
    name: 'Pink neon',
    theme: { backgroundColor: '#0c0b10', surfaceColor: '#17151e', textColor: '#f3f0f5', primaryColor: '#ffffff', accentColor: '#ff4fa8' },
  },
  {
    id: 'green',
    name: 'White on green',
    theme: { backgroundColor: '#183a2d', surfaceColor: '#21493a', textColor: '#eef3ee', primaryColor: '#ffffff', accentColor: '#f6f1df' },
  },
];

const svg = (body: string) =>
  `<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${body}</svg>`;

/** Line-art icons, drawn in the accent colour through currentColor. */
const ICONS = {
  burger: svg(
    '<path d="M10 28c0-10 10-17 22-17s22 7 22 17z"/><path d="M22 18l2 1.5M31 15.5l2 1M40 18.5l2 1.5M27 22l2 1M36 22.5l2 1"/>' +
      '<path d="M8 33c3 3 5-2 8 0s5 3 8 0 5-3 8 0 5 3 8 0 5-3 8 0 5 3 8 0"/><path d="M18 33l9 7 9-7z" fill="currentColor"/>' +
      '<rect x="10" y="38" width="44" height="7" rx="3.5"/><path d="M10 48h44v1a6 6 0 0 1-6 6H16a6 6 0 0 1-6-6z"/>',
  ),
  hotdog: svg(
    '<path d="M6 33c-3 0-3-8 3-8h46c6 0 6 8 3 8"/><path d="M8 33v2c0 7 5 11 12 11h24c7 0 12-4 12-11v-2z"/>' +
      '<path d="M12 29.5l3.5-2.5 3.5 2.5 3.5-2.5 3.5 2.5 3.5-2.5 3.5 2.5 3.5-2.5 3.5 2.5 3.5-2.5 3.5 2.5 3.5-2.5 3.5 2.5"/><path d="M16 40h32"/>',
  ),
  fries: svg(
    '<path d="M20 30V10.5l4-1.5V30M27 30V6l4 1v23M34 30V8l4-1.5V30M41 30V11l4 1.5V30"/>' +
      '<path d="M14 26h36l-4 30H18z"/><path d="M24 38c4 4 12 4 16 0"/>',
  ),
  drink: svg(
    '<path d="M16 22h32"/><path d="M18 22c0-5 6-8 14-8s14 3 14 8"/><path d="M34 14l3-10h7"/>' +
      '<path d="M19 26l3 30h20l3-30z"/><path d="M20.5 34h23"/><circle cx="29" cy="42" r="1.6"/><circle cx="35" cy="47" r="1.6"/><circle cx="31" cy="51" r="1.2"/>',
  ),
  pizza: svg(
    '<path d="M32 58L9 15c15-8 31-8 46 0z"/><path d="M12.5 21.5c13-6 26-6 39 0"/>' +
      '<circle cx="26" cy="29" r="3.2"/><circle cx="39" cy="31" r="3.2"/><circle cx="32" cy="43" r="3.2"/>',
  ),
  coffee: svg(
    '<path d="M12 27h34v12a14 14 0 0 1-14 14h-6a14 14 0 0 1-14-14z"/><path d="M46 31h3a6 6 0 0 1 0 12h-4"/><path d="M8 58h44"/>' +
      '<path d="M22 20c-3-4 3-6 0-11M29 20c-3-4 3-6 0-11M36 20c-3-4 3-6 0-11"/>',
  ),
  dessert: svg(
    '<path d="M21 33l11 25 11-25"/><path d="M26 36l9 9M38 36l-11 13"/>' +
      '<path d="M19 33a8 8 0 0 1 3-13 10 10 0 0 1 20 0 8 8 0 0 1 3 13z"/><path d="M32 10c0-3 2-5 5-6"/><circle cx="32" cy="10" r="0.1"/>',
  ),
  chicken: svg(
    '<path d="M28 38c-7-6-7-18 1-26s20-8 25-2 5 18-3 25-17 9-23 3z"/><path d="M29 38L18 49"/>' +
      '<circle cx="14" cy="48" r="3.5"/><circle cx="18.5" cy="53" r="3.5"/><path d="M38 18c4-2 9 0 10 4"/>',
  ),
  salad: svg(
    '<path d="M7 32h50c0 13-11 22-25 22S7 45 7 32z"/><path d="M24 58h16"/>' +
      '<path d="M15 32c0-8 6-12 13-11 M27 32c1-10 9-15 17-12 M38 32c2-5 7-7 12-5"/><circle cx="22" cy="27" r="3"/>',
  ),
  plate: svg(
    '<path d="M8 47h48"/><path d="M12 47a20 20 0 0 1 40 0"/><path d="M32 27v-4M27 23h10"/><path d="M5 53h54"/><path d="M20 40c2-4 5-6 9-7"/>',
  ),
};

type IconName = keyof typeof ICONS;

/** Keywords, English and Arabic, matched in order: the more specific foods come first. */
const KEYWORDS: [IconName, RegExp][] = [
  ['hotdog', /hot ?dog|sausage|frank|هوت ?دو[غج]|نقانق|سجق/],
  ['burger', /burger|برجر|برغر|بيرغر|بيرجر/],
  ['pizza', /pizza|بيتزا/],
  ['chicken', /chicken|wing|tender|nugget|دجاج|أجنحة|اجنحة|ناجت|فراخ/],
  ['fries', /fries|frie|side|snack|starter|بطاطا|بطاطس|مقبلات|جانبي/],
  ['salad', /salad|green|veg|bowl|سلط|خضار/],
  ['coffee', /coffee|café|cafe|tea|espresso|latte|hot drink|قهوة|شاي|ساخن/],
  ['dessert', /dessert|sweet|ice ?cream|shake|sundae|cake|حلو|بوظة|آيس|ايس|شيك|كيك/],
  ['drink', /drink|beverage|soda|juice|cola|lemonade|refresh|مشروب|عصير|صودا|ليموناد|بارد/],
];

export function nbIconName(section: MenuSection): IconName {
  const text = `${section.title ?? ''} ${section.titleAr ?? ''}`.toLowerCase();
  return KEYWORDS.find(([, re]) => re.test(text))?.[0] ?? 'plate';
}

/** The section's picture in a round frame when it has one, otherwise its line-art icon. */
export function nbArt(ctx: RenderContext, section: MenuSection, cls: string): string {
  const photo = image(ctx, section.imageMediaId, `${cls}-img`);
  if (photo) return `<span class="${cls} ${cls}-photo">${photo}</span>`;
  return `<span class="${cls}">${ICONS[nbIconName(section)]}</span>`;
}

/** A section block: icon (on its first appearance), script heading, then the item lines. */
export function nbBlock(
  ctx: RenderContext,
  b: MenuBlock,
  p: string,
  opts: { leader: boolean; art: boolean },
): string {
  const titled = hasTitle(b.section);
  const art = titled && opts.art && !b.continued ? nbArt(ctx, b.section, `${p}-art`) : '';
  const head = titled
    ? `<div class="${p}-head">${art}<div class="${p}-htext">${sectionTitle(ctx, b.section, `${p}-st`)}${
        b.continued ? '' : sectionSubtitle(ctx, b.section, `${p}-sub`)
      }</div></div>`
    : '';
  return `<div class="${p}-sec${b.continued ? ` ${p}-cont` : ''}">${head}<div class="${p}-items">${b.items
    .map((i) => nbItem(ctx, i, p, opts.leader))
    .join('')}</div></div>`;
}

/**
 * One item line. A single price stays on the name's line, right-aligned, while a long name
 * shrinks and wraps beside it. Sizes get their own row under the name, before the description.
 */
export function nbItem(ctx: RenderContext, item: MenuItemContent, p: string, leader: boolean): string {
  if (!item.variants.length) return listItem(ctx, item, p, leader);
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="${p}-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="${p}-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="${p}-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  const badges = badge(ctx, item, `${p}-badge`);
  const sizes = item.variants
    .map(
      (v) =>
        `<span class="${p}-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span class="${p}-price" dir="ltr">${escapeHtml(
          formatMenuPrice(v.price, ctx.board.settings.currency),
        )}</span></span>`,
    )
    .join('');
  return [
    `<div class="${p}-item ${p}-item-sized">`,
    `<div class="${p}-line">${name}${badges ? `<span class="${p}-badges">${badges}</span>` : ''}</div>`,
    second,
    `<div class="${p}-sizes ${p}-sizerow">${sizes}</div>`,
    description(ctx, item, `${p}-desc`),
    '</div>',
  ].join('');
}

/**
 * `listColumns` plus what it does not see: the sizes row under an item with variants, and a
 * second line for a name too long for its column (`nameChars` capitals fit on one line).
 */
export function nbColumns(
  board: MenuBoardContent,
  columns: MenuColumn[],
  opts: { sizeRow: number; nameLine: number; nameChars: number },
): MenuColumn[] {
  const extra = (items: MenuItemContent[]) =>
    items.reduce(
      (sum, i) =>
        sum +
        (i.variants.length ? opts.sizeRow : 0) +
        ((board.settings.language === 'ar' ? 0 : i.title.trim().length) > opts.nameChars ? opts.nameLine : 0),
      0,
    );
  return columns.map((c) => ({
    capacity: c.capacity,
    blockCost: (section, items, continued) => c.blockCost(section, items, continued) + extra(items),
  }));
}

/** The big title, scaled down to stay on one line, with the script tagline under it. */
export function nbHeader(ctx: RenderContext, p: string, maxChars: number): string {
  const { board } = ctx;
  const title = board.settings.title ?? board.name;
  const scale = fitScale(ctx.showEn ? title : board.settings.titleAr || title, maxChars, 0.45);
  return [
    `<div class="${p}-header">`,
    `<div class="${p}-titlebox" style="font-size:${scale.toFixed(3)}em">`,
    bilingual(ctx, title, board.settings.titleAr, `${p}-title`, 'h1'),
    '</div>',
    bilingual(ctx, board.brand.tagline, board.brand.taglineAr, `${p}-tag`),
    '</div>',
  ].join('');
}

/** The brand as a small accent footnote. */
export function nbFoot(ctx: RenderContext, p: string): string {
  const brand = bilingual(ctx, ctx.board.brand.name, ctx.board.brand.nameAr, `${p}-brand`, 'span');
  return brand ? `<div class="${p}-foot">${brand}</div>` : '';
}

/** Extra height the header takes on a bilingual board, in em. */
export function nbHeaderExtra(board: MenuBoardContent): number {
  const both = board.settings.language === 'both';
  return (both && board.settings.titleAr ? 3.6 : 0) + (both && board.brand.taglineAr ? 1.9 : 0);
}

/** Bilingual lists are set smaller (class `nb-both`) so a screen still holds a fair number of items. */
export const NB_BOTH_SCALE = 0.88;

export function nbListScale(board: MenuBoardContent): number {
  return board.settings.language === 'both' ? NB_BOTH_SCALE : 1;
}

export function nbBothClass(ctx: RenderContext): string {
  return ctx.showEn && ctx.showAr ? ' nb-both' : '';
}

/** Styles every design of the set shares, under its own scope and class prefix. */
export function nbCss(S: string, p: string): string {
  return `
${S}{--nb-rule:color-mix(in srgb,var(--mb-text) 38%,transparent)}
${S} .${p}-header{text-align:center;flex-shrink:0}
${S} .${p}-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-primary);
  text-transform:uppercase;letter-spacing:.06em;line-height:1;white-space:nowrap}
${S} .${p}-title.mb-ar{letter-spacing:0;line-height:1.35}
${S} .${p}-tag{font-family:var(--mb-font-script);color:var(--mb-accent);line-height:1.3}
${S} .${p}-foot{position:absolute;z-index:2;bottom:1.4em;inset-inline-end:3em;display:flex;gap:.8em;align-items:baseline;
  color:var(--mb-accent);font-size:.95em;letter-spacing:.08em}
${S} .${p}-brand.mb-2nd{letter-spacing:0}
${S} .${p}-art{display:block;color:var(--mb-accent)}
${S} .${p}-art svg{display:block;width:100%;height:100%;overflow:visible}
${S} .${p}-art-photo{border-radius:50%;overflow:hidden;border:.18em solid var(--mb-accent);padding:.25em;background:var(--mb-bg)}
${S} .${p}-art-img{display:block;width:100%;height:100%;object-fit:cover;border-radius:50%}
${S} .${p}-st{font-family:var(--mb-font-script);color:var(--mb-accent);line-height:1.25}
${S} .${p}-sub{opacity:.7;font-size:.9em;margin-top:.2em}
${S} .${p}-name{text-transform:uppercase;font-weight:600;letter-spacing:.03em;color:var(--mb-primary)}
${S} .${p}-name.mb-ar{text-transform:none;letter-spacing:0}
${S} .${p}-name2{color:var(--mb-primary)}
${S} .${p}-price{color:var(--mb-accent);font-weight:700}
${S} .${p}-old{color:var(--mb-text)}
${S} .${p}-size small{color:var(--mb-text)}
${S} .${p}-desc{opacity:.78;font-weight:400}
${S} .${p}-line{flex-wrap:nowrap}
${S} .${p}-line>.${p}-price,${S} .${p}-line>.${p}-pricebox{margin-inline-start:auto;flex-shrink:0}
${S} .${p}-sizerow{flex-wrap:wrap;gap:.2em 1.1em;margin-top:.15em;white-space:normal}
${S} .${p}-sizerow .${p}-size{white-space:nowrap}
${S}.mb-v-neon .${p}-art,${S}.mb-v-neon .${p}-st,${S}.mb-v-neon .${p}-tag{
  filter:drop-shadow(0 0 .12em var(--mb-accent)) drop-shadow(0 0 .45em color-mix(in srgb,var(--mb-accent) 60%,transparent))}
${S}.mb-v-neon .${p}-title{text-shadow:0 0 .12em rgba(255,255,255,.55)}
`;
}

/* ------------------------------------------------------------------------- the design */

const S = '.mb-t-night-bites-columns';

export const nightBitesColumns: MenuTemplate = {
  id: 'night-bites-columns',
  name: 'Night Bites · Four columns',
  description:
    'Flat charcoal with a huge white MENU, four columns split by thin rules, each under a line-art icon and a script heading. Matches the other Night Bites screens.',
  defaultTheme: NB_THEME,
  variants: NB_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { ...NB_FAMILY, layout: 'Four columns' } satisfies MenuFamily,
  uses: { featured: false, sectionImages: true, brand: true, decorations: [] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    return packColumns(
      groupBySection(board),
      nbColumns(board, listColumns(board, {
        count: 4,
        // Portrait columns are set 10% larger, so they hold 10% fewer of these em.
        capacity: (portrait ? 41 - nbHeaderExtra(board) / 2 : 48 - nbHeaderExtra(board)) / nbListScale(board),
        heading: 4,
        item: { nameEm: 1.45, descEm: 0.95, perLine: portrait ? 40 : 42, gap: 1.45 },
        // The icon sits over a section's first block only; a continued block repeats just the heading.
        extra: (section, continued) => (!continued && hasTitle(section) ? 7.2 : 0),
      }), { sizeRow: 1.35 * 1.25, nameLine: 1.45 * 1.15, nameChars: portrait ? 20 : 18 }),
      { balance: true },
    );
  },

  render(ctx) {
    const cols = ctx.page.columns
      .map((column) => `<div class="nba-col">${column.map((b) => nbBlock(ctx, b, 'nba', { leader: false, art: true })).join('')}</div>`)
      .join('');
    return `${nbHeader(ctx, 'nba', ctx.orientation === 'portrait' ? 10 : 16)}<div class="nba-cols${nbBothClass(ctx)}">${cols}</div>${nbFoot(ctx, 'nba')}`;
  },

  css: `
${S}{padding:2.2em 3.5em 3.4em}
${nbCss(S, 'nba')}
${S} .nba-titlebox{font-size:1em}
${S} .nba-title{font-size:7.2em}
${S} .nba-title.mb-2nd{font-size:2.6em;margin-top:.1em}
${S} .nba-tag{font-size:1.75em;margin-top:.1em}
${S} .nba-tag.mb-2nd{font-size:1.4em;margin-top:0}
${S} .nba-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(4,1fr);margin-top:2.6em}
${S} .nba-cols.nb-both{font-size:.88em;margin-top:2em}
${S}.mb-portrait{padding:3em 3em 3.6em}
${S}.mb-portrait .nba-cols{grid-template-columns:1fr 1fr;grid-template-rows:1fr 1fr;margin-top:3em;font-size:1.1em}
${S}.mb-portrait .nba-cols.nb-both{font-size:.968em}
${S} .nba-col{min-width:0;min-height:0;display:flex;flex-direction:column;gap:1.6em;padding:0 2.2em}
${S} .nba-col+.nba-col{border-inline-start:.09em solid var(--nb-rule)}
${S}.mb-portrait .nba-col:nth-child(3){border-inline-start:0}
${S}.mb-portrait .nba-col:nth-child(n+3){border-top:.09em solid var(--nb-rule);padding-top:2.4em}
${S}.mb-portrait .nba-col:nth-child(-n+2){padding-bottom:1.6em}
${S} .nba-head{display:flex;flex-direction:column;align-items:center;text-align:center;margin-bottom:1.2em}
${S} .nba-art{width:6.4em;height:6.4em;margin-bottom:.4em}
${S} .nba-st{font-size:2.6em}
${S} .nba-st.mb-2nd{font-size:1.6em}
${S} .nba-cont .nba-st{font-size:1.7em}
${listCss(S, 'nba', { name: '1.45em', price: '1.35em', desc: '.95em', leader: 'none' })}
${S} .nba-item{margin-bottom:1.45em}
${S} .nba-name{flex:0 1 auto}
${S} .nba-name2{font-size:1.05em}
`,
};
